import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Button } from '../../../../components/ui';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { v2Cares } from '../../../../lib/cares';
import { toCareOverlap, careErrorMessage, type CareOverlapInfo } from '../../../../lib/careOverlap';
import { CareOverlapWarning } from '../../../../components/care/CareOverlapWarning';
import { CARE_STATUS_OPTIONS } from './constants';
import { BkPicker } from './BkPicker';
import { DateInput } from '../../../../components/DateInput';
import { StartdatumAutoHint } from '../../../../components/care/StartdatumAutoHint';

/** Status „Bestätigt" — dann setzt die API das PB-Startdatum auf das Enddatum. */
const CARE_STATUS_CONFIRMED = 2;

/**
 * "Neue Betreuung anlegen" — als Drawer, kontextabhängig:
 *  - PB-Kontext (userRole=2): pb_user_id fix, im Picker wird die BK gewählt.
 *  - BK-Kontext (userRole=1): bk_user_id fix, im Picker wird der PB gewählt
 *    ("PB auswählen").
 */
interface CreateCareModalProps {
  dark: boolean;
  /** ID des Users, in dessen Profil der Drawer geöffnet wurde (PB oder BK). */
  pbUserId: number;
  /** Rolle des Profil-Users: 2 = PB (Default), 1 = BK. */
  userRole?: number;
  /** Vorausgewählter Partner (z. B. BK aus dem Matching-Board). */
  initialPartnerId?: number | null;
  onCreated: () => void;
  /**
   * Die API hat das Startdatum des PB automatisch gesetzt (nur wenn die
   * Betreuung als „Bestätigt" angelegt wurde) — neuer Wert als YYYY-MM-DD.
   */
  onPbStartdatumChanged?: (startdatum: string) => void;
  onClose: () => void;
}

export function CreateCareModal({ dark, pbUserId, userRole = 2, initialPartnerId = null, onCreated, onPbStartdatumChanged, onClose }: CreateCareModalProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const isBkContext = userRole === 1;
  const [partnerId, setPartnerId] = useState<number | null>(initialPartnerId);
  const [startdatum, setStartdatum] = useState('');
  const [enddatum, setEnddatum] = useState('');
  const [status, setStatus] = useState(3); // Default: Vorbereitung
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Überlappung > 1 Tag: Warnung anzeigen, der nächste Klick legt trotzdem an.
  const [overlap, setOverlap] = useState<CareOverlapInfo | null>(null);
  /** Zeitraum/Partner geändert → eine frühere Bestätigung gilt nicht mehr. */
  const resetOverlap = () => setOverlap(null);

  const endRequired = status === 0 || status === 2;

  // Sperre gegen Doppel-Absenden (Enter + Klick, Doppelklick): `saving` aus dem
  // State greift erst nach dem nächsten Render — bis dahin legte ein zweites
  // Submit dieselbe Betreuung noch einmal an.
  const submitting = useRef(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting.current) return;
    setError(null);
    if (!partnerId) {
      setError(tr(isBkContext ? 'pflege.cares.create_pb_required' : 'pflege.cares.create_bk_required'));
      return;
    }
    if (!startdatum) { setError(tr('pflege.cares.create_startdatum_required')); return; }
    if (endRequired && !enddatum) { setError(tr('pflege.cares.create_enddatum_required')); return; }
    submitting.current = true;
    setSaving(true);
    const pbId = isBkContext ? partnerId : pbUserId;
    let pbStartdatum: string | null = null;
    try {
      // v2: Überlappung mit anderen Betreuungen des PB ist erlaubt (v1 lehnte
      // sie ab). Bei Status „Bestätigt" setzt die API das PB-Startdatum
      // automatisch auf das Enddatum dieser Betreuung (Ticket cmuy4wq2y).
      const created = await v2Cares.create({
        pb_user_id: pbId,
        bk_user_id: isBkContext ? pbUserId : partnerId,
        startdatum,
        enddatum: enddatum || null,
        status,
        notify,
        ...(overlap ? { confirm_overlap: true } : {}),
      });
      pbStartdatum = created.startdatum;
    } catch (err) {
      const needsConfirm = toCareOverlap(err);
      if (needsConfirm) {
        // Mehr als ein gemeinsamer Tag → warnen; erneutes Absenden bestätigt.
        setOverlap(needsConfirm);
      } else {
        setError(careErrorMessage(err, tr('pflege.cares.create_error')));
      }
      setSaving(false);
      submitting.current = false;
      return;
    }
    // Bestätigt → die API hat das PB-Startdatum verlegt; die Sidebar des
    // geöffneten PB-Profils sofort nachziehen (nur im PB-Kontext).
    if (!isBkContext && status === CARE_STATUS_CONFIRMED && pbStartdatum) {
      onPbStartdatumChanged?.(pbStartdatum);
    }
    setSaving(false);
    onCreated();
  }, [partnerId, startdatum, enddatum, status, notify, pbUserId, isBkContext, endRequired, tr, onCreated, onPbStartdatumChanged, overlap]);

  const inputStyle: React.CSSProperties = {
    fontSize: 13, padding: '8px 12px', width: '100%', boxSizing: 'border-box',
    border: `1px solid ${t.border}`, borderRadius: 8, background: t.inputBg,
    color: t.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
  };

  return (
    <Drawer open onClose={onClose} width={480} dark={dark}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${t.border}`, flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: t.heading }}>
            {tr('pflege.cares.create_modal_title')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tr('common.action.close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.muted, fontSize: 18, lineHeight: 1, padding: 4 }}
          >×</button>
        </div>

        {/* Body (scrollbar) */}
        <form id="create-care-form" onSubmit={handleSubmit} style={{ padding: 24, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Partner-Picker: BK-Kontext wählt den PB, PB-Kontext die BK */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.muted, display: 'block', marginBottom: 5 }}>
              {tr(isBkContext ? 'pflege.cares.create_pb_label' : 'pflege.cares.create_bk_label')}
            </label>
            <BkPicker
              dark={dark}
              pbUserId={pbUserId}
              role={isBkContext ? 'PB' : 'BK'}
              value={partnerId}
              onChange={(id) => { setPartnerId(id); resetOverlap(); }}
            />
          </div>

          {/* Startdatum */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.muted, display: 'block', marginBottom: 5 }}>
              {tr('pflege.cares.create_startdatum_label')}
            </label>
            <DateInput ariaLabel={tr('pflege.cares.create_startdatum_label')} value={startdatum} onChange={v => { setStartdatum(v); resetOverlap(); }} style={inputStyle} />
          </div>

          {/* Enddatum */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.muted, display: 'block', marginBottom: 5 }}>
              {tr('pflege.cares.create_enddatum_label')}
              {!endRequired && <span style={{ fontWeight: 400, color: t.muted }}> (optional)</span>}
            </label>
            <DateInput ariaLabel={tr('pflege.cares.create_enddatum_label')} value={enddatum} onChange={v => { setEnddatum(v); resetOverlap(); }} style={inputStyle} />
          </div>

          {/* Status */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.muted, display: 'block', marginBottom: 5 }}>
              {tr('pflege.cares.create_status_label')}
            </label>
            <select value={status} onChange={e => setStatus(Number(e.target.value))} style={inputStyle}>
              {CARE_STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{tr(opt.labelKey)}</option>
              ))}
            </select>
          </div>

          {/* Checkboxes */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: t.text, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={notify}
              onChange={e => setNotify(e.target.checked)}
              style={{ cursor: 'pointer', accentColor: BRAND, width: 15, height: 15 }}
            />
            {tr('pflege.cares.create_notify_label')}
          </label>
          <StartdatumAutoHint dark={dark} status={status} enddatum={enddatum} />

          {overlap && <CareOverlapWarning overlap={overlap} dark={dark} />}

          {error && (
            <div style={{ padding: '8px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 7, fontSize: 12.5, color: '#b42318' }}>
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${t.border}`, background: t.cardMuted, flexShrink: 0, display: 'flex', gap: 10 }}>
          <Button variant="primary" dark={dark} disabled={saving} style={{ flex: 1, justifyContent: 'center' }} onClick={() => {
            (document.getElementById('create-care-form') as HTMLFormElement | null)?.requestSubmit();
          }}>
            {saving ? '…' : overlap ? tr('pflege.cares.overlap.create_anyway') : tr('pflege.cares.create')}
          </Button>
          <Button variant="secondary" dark={dark} onClick={onClose}>
            {tr('common.action.cancel')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
